import type { ContentEntityType } from '@/types/content-lifecycle';

/** Read invalidation only; never write, receipt, or audio-cleanup authority. */
export interface ContentObservation {
  ownerId: string;
  entityType?: ContentEntityType;
  entityId?: string;
}
const listeners = new Set<(hint: ContentObservation) => void>();
let channel: BroadcastChannel | null = null;
const channelName = 'watch-him-work-content-observations';
function validHint(value: unknown): value is ContentObservation {
  if (!value || typeof value !== 'object') return false;
  const hint = value as ContentObservation;
  return typeof hint.ownerId === 'string' && hint.ownerId.length > 0 && hint.ownerId.length <= 200 &&
    Object.keys(value).every((key) => ['ownerId', 'entityType', 'entityId'].includes(key)) &&
    ((hint.entityType === undefined && hint.entityId === undefined) ||
      (['prayer', 'bookmark', 'highlight'].includes(hint.entityType ?? '') &&
        typeof hint.entityId === 'string' && hint.entityId.length > 0 && hint.entityId.length <= 200));
}
function deliver(hint: ContentObservation): void {
  for (const listener of [...listeners]) {
    try { listener(hint); } catch { /* observations cannot change a committed save */ }
  }
}
function getChannel(): BroadcastChannel | null {
  try {
    if (typeof window === 'undefined' || typeof window.BroadcastChannel === 'undefined') return null;
    if (!channel) {
      channel = new window.BroadcastChannel(channelName);
      channel.onmessage = (event: MessageEvent<unknown>) => {
        if (validHint(event.data)) deliver(event.data); // no rebroadcast
      };
    }
    return channel;
  } catch { return null; }
}
export function notifyContentCommitted(hint: ContentObservation): void {
  try {
    if (!validHint(hint)) return;
    deliver(hint);
    getChannel()?.postMessage(hint);
  } catch { /* channel failure cannot change durable success */ }
}
export function subscribeContentObservations(listener: (hint: ContentObservation) => void): () => void {
  try { listeners.add(listener); getChannel(); } catch { /* foreground reread remains */ }
  return () => { try { listeners.delete(listener); } catch { /* best effort */ } };
}
